import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEntity } from '../../contexts/EntityContext';
import { api, AccountingPeriod } from '../../lib/api';
import { ConfirmModal } from '../../components/ui/Modal';
import { useToast } from '../../components/ui/Toast';
import PeriodFormModal from '../../components/PeriodFormModal';

function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export default function PeriodsPage() {
  const queryClient = useQueryClient();
  const { currentEntity } = useEntity();
  const { success, error: showError } = useToast();
  const [showForm, setShowForm] = useState(false);
  const [editingPeriod, setEditingPeriod] = useState<AccountingPeriod | null>(null);
  const [deletingPeriod, setDeletingPeriod] = useState<AccountingPeriod | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['periods', currentEntity?.id],
    queryFn: () => api.admin.periods.list(currentEntity!.id),
    enabled: !!currentEntity,
  });

  const deleteMutation = useMutation({
    mutationFn: (periodId: string) =>
      fetch(`/api/admin/periods/${periodId}`, {
        method: 'DELETE',
        headers: { 'X-Entity-Id': currentEntity!.id },
        credentials: 'include',
      }).then(r => r.json()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['periods'] });
      success('Period deleted');
      setDeletingPeriod(null);
    },
    onError: () => showError('Failed to delete period'),
  });

  if (!currentEntity) return null;

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Accounting Periods - {currentEntity.code}</h1>
        <button className="btn btn-primary" onClick={() => { setEditingPeriod(null); setShowForm(true); }}>
          New Period
        </button>
      </div>

      {isLoading ? (
        <p className="text-muted">Loading...</p>
      ) : (
        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Status</th>
                <th>Closed By</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data?.data.map((period: AccountingPeriod) => (
                <tr key={period.id}>
                  <td><strong>{period.name}</strong></td>
                  <td>{formatDate(period.startDate)}</td>
                  <td>{formatDate(period.endDate)}</td>
                  <td>
                    <span className={`badge badge-${period.status}`}>{capitalize(period.status)}</span>
                  </td>
                  <td className="text-muted">
                    {period.closedAt ? `${period.closedByName} on ${formatDate(period.closedAt)}` : '-'}
                  </td>
                  <td className="text-right">
                    <div className="flex gap-2 justify-end">
                      <button
                        className="btn btn-secondary px-3 py-1 text-[13px]"
                        onClick={() => { setEditingPeriod(period); setShowForm(true); }}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-danger px-3 py-1 text-[13px]"
                        onClick={() => setDeletingPeriod(period)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {data?.data.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center text-muted">No periods</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <PeriodFormModal
          period={editingPeriod}
          entityId={currentEntity.id}
          onClose={() => { setShowForm(false); setEditingPeriod(null); }}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['periods'] });
            setShowForm(false);
            setEditingPeriod(null);
          }}
        />
      )}

      <ConfirmModal
        isOpen={!!deletingPeriod}
        onClose={() => setDeletingPeriod(null)}
        onConfirm={() => deletingPeriod && deleteMutation.mutate(deletingPeriod.id)}
        title="Delete Period"
        message={`Are you sure you want to delete "${deletingPeriod?.name}"? This cannot be undone.`}
        confirmText="Delete"
        confirmVariant="danger"
        isLoading={deleteMutation.isPending}
      />

    </div>
  );
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
